/**
 * Prototype-only: how the Revenue page lists statements and sales entries.
 *
 *  - one-list — both in one list, told apart by an Entry type column and filter
 *    (the default; Nick leans towards it).
 *  - tabs — Statements / Sales entries tabs, each list with the columns that
 *    apply to it.
 *
 * And, in the tabs version, which header buttons show:
 *
 *  - both — Add sales entry and Upload new statement, whichever tab is open
 *    (the default).
 *  - per-tab — one primary button, the open tab's: Upload new statement on
 *    Statements, Add sales entry on Sales entries.
 *
 * Persisted, so a refresh keeps the version the reviewer picked.
 *
 * Scaffolding for the contract-out-licensing prototype — delete with the rest of
 * src/prototypes/contract-out-licensing/.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

export const REVENUE_VARIANTS = {
  "one-list": "One list",
  tabs: "Tabs",
} as const;

export type RevenueListVariant = keyof typeof REVENUE_VARIANTS;

export const REVENUE_ACTIONS_VARIANTS = {
  both: "Both",
  "per-tab": "One per tab",
} as const;

export type RevenueActionsVariant = keyof typeof REVENUE_ACTIONS_VARIANTS;

type RevenueVariantState = {
  revenueVariant: RevenueListVariant;
  setRevenueVariant: (variant: RevenueListVariant) => void;
  actionsVariant: RevenueActionsVariant;
  setActionsVariant: (variant: RevenueActionsVariant) => void;
};

export const useRevenueListVariant = create<RevenueVariantState>()(
  persist(
    (set) => ({
      revenueVariant: "one-list",
      setRevenueVariant: (revenueVariant) => set({ revenueVariant }),
      actionsVariant: "both",
      setActionsVariant: (actionsVariant) => set({ actionsVariant }),
    }),
    { name: "prototype-revenue-list-variant-v2" },
  ),
);
